// Angular
import { Component, OnInit, HostListener } from '@angular/core';
// Libs
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
// Models
import { ISession } from '@core/models-v2/auth/session.interface';
import { IWishlist } from '@core/services-v2/wishlist/models/wishlist-response.interface';
import { Articulo } from '../../interfaces/articulos.response';
// Services
import { SessionService } from '@core/services-v2/session/session.service';
import { WishlistApiService } from '@core/services-v2/wishlist/wishlist-api.service';
import { ISelectedProduct } from './selected-product.interface';

@Component({
  selector: 'app-agregar-lista-productos-unitaria-modal',
  templateUrl: './agregar-lista-productos-unitaria-modal.component.html',
  styleUrls: ['./agregar-lista-productos-unitaria-modal.component.scss'],
})
export class AgregarListaProductosUnitariaModalComponent implements OnInit {
  session!: ISession;
  lista!: IWishlist; // Lista a la que se agregarán productos
  isLoading = false;
  innerWidth: number = window.innerWidth;

  // Productos seleccionados - usando la interfaz Articulo del componente existente
  selectedProducts: ISelectedProduct[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private messageService: MessageService,
    // Services V2
    private readonly sessionService: SessionService,
    private readonly wishlistApiService: WishlistApiService
  ) {}

  ngOnInit(): void {
    this.session = this.sessionService.getSession();

    // Obtener la lista desde la configuración del modal
    if (this.config.data?.lista) {
      this.lista = this.config.data.lista;
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de configuración',
        detail: 'No se especificó una lista de destino.',
        life: 5000,
        icon: 'pi pi-times-circle',
      });
      this.close(false);
      return;
    }
  }

  /**
   * Manejar cambio en la selección de productos desde el componente de búsqueda
   */
  onFiltrosCambiados(articulo: any): void {
    console.log('Producto recibido:', articulo);

    if (!articulo) {
      console.log('No se recibió ningún producto');
      return;
    }

    // Verificar que el producto tenga las propiedades necesarias
    if (!articulo.sku || !articulo.name) {
      console.log('Producto incompleto:', articulo);
      this.messageService.add({
        severity: 'error',
        summary: 'Producto inválido',
        detail: 'El producto seleccionado no tiene la información necesaria',
        life: 3000,
        icon: 'pi pi-times-circle',
      });
      return;
    }

    const currentSelectedSkus = this.selectedProducts.map(
      (product) => product.sku
    );

    if (!currentSelectedSkus.includes(articulo.sku)) {
      // Obtener la imagen correctamente con más opciones
      let image = '';

      // Intentar diferentes formas de obtener la imagen
      if (articulo.images) {
        if (
          articulo.images['250'] &&
          Array.isArray(articulo.images['250']) &&
          articulo.images['250'][0]
        ) {
          image = articulo.images['250'][0];
        } else if (
          articulo.images['500'] &&
          Array.isArray(articulo.images['500']) &&
          articulo.images['500'][0]
        ) {
          image = articulo.images['500'][0];
        } else if (typeof articulo.images === 'string') {
          image = articulo.images;
        }
      } else if (articulo.image) {
        image = articulo.image;
      } else if (articulo.preview) {
        image = articulo.preview;
      } else if (articulo.thumbnail) {
        image = articulo.thumbnail;
      }

      // Crear el producto a agregar
      const newProduct: ISelectedProduct = {
        image: image || '/assets/images/no-image.png',
        sku: articulo.sku,
        name: articulo.name,
      };

      console.log('Agregando producto:', newProduct);
      this.selectedProducts.push(newProduct);

      this.messageService.add({
        severity: 'success',
        summary: 'Producto agregado',
        detail: `${articulo.name} agregado a la lista`,
        life: 2000,
        icon: 'pi pi-check-circle',
      });

      console.log('Lista actualizada:', this.selectedProducts);
    } else {
      console.log('Producto ya existe en la lista:', articulo.sku);
      this.messageService.add({
        severity: 'warn',
        summary: 'Producto ya seleccionado',
        detail: `${articulo.name} ya se encuentra en la lista`,
        life: 2000,
        icon: 'pi pi-exclamation-triangle',
      });
    }
  }

  /**
   * Deseleccionar producto de la lista.
   * @param productIndex
   */
  unselectProduct(productIndex: number): void {
    if (productIndex >= 0 && productIndex < this.selectedProducts.length) {
      const removedProduct = this.selectedProducts[productIndex];
      this.selectedProducts.splice(productIndex, 1);

      // Actualizar el componente de búsqueda creando un nuevo array
      this.selectedProducts = [...this.selectedProducts];

      this.messageService.add({
        severity: 'warn',
        summary: 'Producto eliminado',
        detail: `${
          removedProduct.name || removedProduct.sku
        } se quitó de la lista`,
        life: 2000,
        icon: 'pi pi-exclamation-triangle',
      });
    }
  }

  /**
   * Limpiar todos los productos seleccionados.
   */
  clearAllProducts(): void {
    this.selectedProducts = [];
    this.messageService.add({
      severity: 'info',
      summary: 'Lista limpiada',
      detail: 'Todos los productos han sido removidos de la lista',
      life: 2000,
      icon: 'pi pi-info-circle',
    });
  }

  guardar(): void {
    if (this.isLoading) {
      return;
    }

    if (this.selectedProducts.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin productos',
        detail: 'Seleccione al menos un producto para agregar a la lista',
        life: 4000,
        icon: 'pi pi-exclamation-triangle',
      });
      return;
    }

    const skus = this.selectedProducts.map((product) => product.sku);

    this.isLoading = true;
    this.addProductsToWishlist(skus);
  }

  /**
   * Agregar productos a la lista existente.
   * @param skus
   */
  private addProductsToWishlist(skus: string[]): void {
    this.wishlistApiService
      .addProductsToWishlist({
        wishlistId: this.lista.id,
        skus: skus,
        documentId: this.session.documentId,
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Productos agregados',
            detail: `${skus.length} productos agregados exitosamente a "${this.lista.name}"`,
            life: 5000,
            icon: 'pi pi-check-circle',
          });
          this.close(true);
          this.isLoading = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error al agregar productos',
            detail:
              'No se pudieron agregar los productos. Intente nuevamente.',
            life: 5000,
            icon: 'pi pi-times-circle',
          });
          this.isLoading = false;
          console.error('Error adding products to wishlist:', error);
        },
      });
  }

  /**
   * Scroll hacia la lista de productos seleccionados
   */
  scrollToSelected(): void {
    setTimeout(() => {
      const selectedSection = document.querySelector(
        '.flex-1.position-relative'
      );
      if (selectedSection) {
        selectedSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  }

  close(hasChanges: boolean): void {
    this.ref.close(hasChanges);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.innerWidth = event.target.innerWidth;
  }
}
